/**
 * Core Error Hierarchy — Clean Architecture
 */

export class AppError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly context: Record<string, unknown>;
  public readonly timestamp: string;

  constructor(
    message: string,
    code: string,
    statusCode = 500,
    context: Record<string, unknown> = {}
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.statusCode = statusCode;
    this.context = context;
    this.timestamp = new Date().toISOString();
  }

  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      statusCode: this.statusCode,
      context: this.context,
      timestamp: this.timestamp,
    };
  }
}

/** Validation errors — invalid input, business rule violations. Status: 400 */
export class ValidationError extends AppError {
  constructor(message: string, context: Record<string, unknown> = {}) {
    super(message, 'VALIDATION_ERROR', 400, context);
    this.name = 'ValidationError';
  }
}

/** Entity not found. Status: 404 */
export class NotFoundError extends AppError {
  constructor(entityName: string, entityId: string) {
    super(
      `${entityName} với ID '${entityId}' không tìm thấy.`,
      'NOT_FOUND',
      404,
      { entityName, entityId }
    );
    this.name = 'NotFoundError';
  }
}

/** Transaction/ACID errors — rollbacks, failures. Status: 409 */
export class TransactionError extends AppError {
  public readonly cause: Error | null;

  constructor(message: string, cause: Error | null = null, context: Record<string, unknown> = {}) {
    super(message, 'TRANSACTION_ERROR', 409, { ...context, cause: cause?.message });
    this.name = 'TransactionError';
    this.cause = cause;
  }
}

/** Concurrency conflict — optimistic lock failure. Status: 409 */
export class ConcurrencyError extends AppError {
  constructor(resource: string, message?: string) {
    super(
      message || `Dữ liệu '${resource}' đã bị thay đổi bởi phiên khác. Vui lòng thử lại.`,
      'CONCURRENCY_ERROR',
      409,
      { resource }
    );
    this.name = 'ConcurrencyError';
  }
}

/** Authorization errors — insufficient permissions. Status: 403 */
export class AuthorizationError extends AppError {
  constructor(message = 'Bạn không có quyền thực hiện thao tác này.', context: Record<string, unknown> = {}) {
    super(message, 'AUTHORIZATION_ERROR', 403, context);
    this.name = 'AuthorizationError';
  }
}

/** Business logic violations. Status: 422 */
export class BusinessRuleError extends AppError {
  constructor(message: string, rule = '', context: Record<string, unknown> = {}) {
    super(message, 'BUSINESS_RULE_ERROR', 422, { rule, ...context });
    this.name = 'BusinessRuleError';
  }
}

/** Infrastructure failures (Firebase, network). Status: 503 */
export class InfrastructureError extends AppError {
  public readonly cause: Error | null;

  constructor(message: string, service: string, cause: Error | null = null) {
    super(message, 'INFRASTRUCTURE_ERROR', 503, { service, cause: cause?.message });
    this.name = 'InfrastructureError';
    this.cause = cause;
  }
}
